import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiCheckCircle, FiLoader, FiAlertCircle } = FiIcons;

const EmailCapture = ({ results }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const formatRM = (val) => new Intl.NumberFormat('en-MY', { 
    style: 'currency', 
    currency: 'MYR', 
    minimumFractionDigits: 0 
  }).format(val);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email locally first
    if (!email) return;
    if (!isValidEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      setStatus('error');
      return;
    }

    // Ensure calculation data exists
    if (!results) {
      setErrorMessage('Please calculate your EPF savings first.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    // Prepare Payload with calculated fields
    const payload = {
      email: email,
      name: "MyEPF User", // Default name mapping
      sources: "MyEPF Calculator", // Default source mapping
      monthly_salary: formatRM(results.inputs.salary),
      age: results.inputs.age,
      years_to_retirement: results.inputs.years,
      monthly_contribution: formatRM(results.monthly),
      annual_contribution: formatRM(results.annual),
      total_savings: formatRM(results.total)
    };

    try {
      console.log('Submitting to Netlify Function proxy...');

      // Call our internal Netlify function
      const response = await fetch('/.netlify/functions/send-to-acumbamail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('Function Response:', data);

      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        throw new Error(data.error || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Submission Error:', error);
      setStatus('error');
      setErrorMessage("We couldn’t submit your request right now. Please try again.");
    }
  };

  return (
    <section id="email-capture" className="py-24 bg-slate-50 scroll-mt-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 text-center relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-50 rounded-full blur-3xl opacity-50"></div>
          
          <AnimatePresence mode="wait">
            {status !== 'success' ? (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative z-10"
              >
                <div className="w-16 h-16 bg-brand-50 text-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiMail} className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-black text-slate-900 mb-3">Get Your Full EPF Report</h2>
                <p className="text-slate-500 text-lg mb-10 max-w-md mx-auto leading-relaxed">
                  Enter your email to receive a detailed EPF savings report and future projections.
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mb-4">
                  <div className="flex-grow relative">
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      className={`w-full px-6 py-4 rounded-xl border transition-all text-lg outline-none ${
                        status === 'error' && (errorMessage.includes('valid') || errorMessage.includes('calculate'))
                        ? 'border-red-300 ring-4 ring-red-500/10' 
                        : 'border-slate-200 focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500'
                      } disabled:bg-slate-50 disabled:cursor-not-allowed`}
                      value={email}
                      disabled={status === 'loading'}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (status === 'error') setStatus('idle');
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading' || !email}
                    className="btn-primary px-8 py-4 text-lg whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed min-w-[180px]"
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center gap-2">
                        <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      'Send My Report'
                    )}
                  </button>
                </form>

                {status === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 flex flex-col items-center gap-1"
                  >
                    <div className="flex items-center gap-2 text-red-500 text-sm font-bold">
                      <SafeIcon icon={FiAlertCircle} className="w-4 h-4" />
                      {errorMessage.includes('submit') ? "Something went wrong" : errorMessage}
                    </div>
                    {errorMessage.includes('submit') && (
                       <p className="text-xs font-medium text-red-400">
                         We couldn’t submit your request right now. Please try again.
                       </p>
                    )}
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 py-8"
              >
                <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheckCircle} className="w-10 h-10" />
                </div>
                <h2 className="text-3xl font-black text-slate-900 mb-3">Check Your Inbox!</h2>
                <p className="text-slate-500 text-lg max-w-md mx-auto leading-relaxed">
                  Your request has been received successfully. We’ll send your EPF report soon.
                </p>
                <button 
                  onClick={() => setStatus('idle')}
                  className="mt-8 text-brand-600 font-bold hover:underline"
                >
                  Send to another email
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default EmailCapture;